import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import { getMovieById, getRelatedMovies, categoryNames, subcategoryNames } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = Number(id);
  const movie = getMovieById(movieId);

  if (!movie) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-500"></i>
            </div>
            <p className="text-foreground-400">内容不存在</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-400 hover:text-primary-300 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const catName = categoryNames[movie.category] || movie.category;
  const subName = subcategoryNames[movie.subcategory] || movie.subcategory;

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-20">
        <article className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catName, href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mb-14">
            <div className="w-full lg:w-[300px] flex-shrink-0">
              <div className="sticky top-24">
                <div className="relative overflow-hidden rounded-xl aspect-[2/3] bg-background-200">
                  <img
                    src={movie.image}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - 牛牛影视`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <header>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mb-2">
                  {movie.title}
                </h1>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <span className="px-2.5 py-1 text-sm bg-primary-500/20 text-primary-400 rounded-md font-semibold">
                    {movie.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{catName}</span>
                  <span className="text-sm text-foreground-500">{subName}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </header>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 p-4 bg-background-100 rounded-xl">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">导演</p>
                  <p className="text-sm text-foreground-200 font-medium">{movie.director}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">集数</p>
                  <p className="text-sm text-foreground-200 font-medium">
                    {movie.episodes > 1 ? `${movie.episodes} 集` : '电影'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">状态</p>
                  <p className="text-sm text-foreground-200 font-medium">{movie.updateInfo}</p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-heading text-foreground-100 mb-3">
                  主演
                </h2>
                <div className="flex flex-wrap gap-2">
                  {movie.cast.map((actor) => (
                    <span
                      key={actor}
                      className="text-sm px-3 py-1.5 rounded-lg bg-background-100 text-foreground-300"
                    >
                      {actor}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-heading text-foreground-100 mb-3">
                  剧情简介
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {movie.description}
                </p>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <section className="pb-14">
              <h2 className="text-xl font-heading text-foreground-100 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {related.map((m) => (
                  <MovieCard key={m.id} movie={m} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.image,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.cast.map((c) => ({
              '@type': 'Person',
              name: c,
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
              worstRating: '0',
            },
          }),
        }}
      />
    </>
  );
}